const REDACTED = '[REDACTED]';
const BEARER_TOKEN_REGEX = /\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi;
const JWT_REGEX = /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\b/g;
const MCP_CREDENTIAL_REGEX = /\bimcp_(?:pending|code|access|refresh|delegated|cursor)_[A-Za-z0-9_-]+\b/g;
const SENSITIVE_NAMES = 'authorization|proxy-authorization|cookie|set-cookie|x-api-key|api[_-]?key|(?:access|refresh|id|activation|activate|reset|invite)[_-]?token|token(?:[_-]?hash)?|jwt|(?:new[_-]?|confirm[_-]?|current[_-]?)?password|secret';
const SENSITIVE_KEY_REGEX = new RegExp(`^(?:${SENSITIVE_NAMES})$`, 'i');
const ESCAPED_SECRET_KEY_REGEX = new RegExp(String.raw`\\["'](?:${SENSITIVE_NAMES})\\["']\s*:`, 'i');
const NAMED_SECRET_REGEX = new RegExp(
  `((?:["']?)(?:${SENSITIVE_NAMES.replace('authorization|proxy-authorization|cookie|set-cookie|', '')})(?:["']?)\\s*[:=]\\s*)("(?:\\\\.|[^"\\\\])*"|'(?:\\\\.|[^'\\\\])*'|[^\\s,;&?#}\\]]+)`,
  'gi',
);

// Scrub at each encoding layer before decoding. A percent-encoded ampersand
// inside a password must not become a query delimiter before its value is hidden.
export function scrubString(input: string): string {
  let result = input;
  for (let i = 0; i < 5; i += 1) {
    result = scrubDecodedString(result);
    const decoded = result.replace(/(?:%[0-9a-f]{2})+/gi, (part) => {
      try { return decodeURIComponent(part); } catch { return part; }
    });
    if (decoded === result) return result;
    result = decoded;
  }
  return scrubDecodedString(result);
}

function scrubDecodedString(input: string): string {
  try {
    const parsed: unknown = JSON.parse(input);
    if (parsed !== null && typeof parsed === 'object') return safeStringify(parsed);
  } catch { /* Freeform diagnostic text is handled below. */ }
  // Omit incomplete escaped bodies instead of risking a leaked value suffix.
  const escapedKey = input.search(ESCAPED_SECRET_KEY_REGEX);
  if (escapedKey >= 0) return scrubString(input.slice(0, escapedKey)) + REDACTED;
  return input
    .replace(/(\b(?:proxy-authorization|authorization|set-cookie|cookie)\s*[:=]\s*)([^\r\n]*)/gi, (_match, prefix: string, value: string) => prefix + (/^Bearer\s/i.test(value) ? 'Bearer ' : '') + REDACTED)
    .replace(BEARER_TOKEN_REGEX, 'Bearer ' + REDACTED)
    .replace(JWT_REGEX, REDACTED)
    .replace(MCP_CREDENTIAL_REGEX, REDACTED)
    .replace(NAMED_SECRET_REGEX, (_match, prefix: string, value: string) => {
      const quote = value.startsWith('"') ? '"' : value.startsWith("'") ? "'" : '';
      return prefix + quote + REDACTED + quote;
    });
}

type SafeStringifyOptions = { maxLength?: number };

export function safeStringify(value: unknown, options: SafeStringifyOptions = {}): string {
  const maxLength = options.maxLength ?? 8192;
  const seen = new WeakSet<object>();
  try {
    const raw = JSON.stringify(value, function (key, val: unknown): unknown {
      if (SENSITIVE_KEY_REGEX.test(key)) return REDACTED;
      // Pydantic errors may echo a whole body in input or a custom error context.
      if (this && Array.isArray(this.loc) && typeof this.type === 'string') {
        if (key === 'input' || key === 'ctx') return REDACTED;
        if (key === 'msg') return 'Invalid value';
      }
      if (val instanceof Error) return sanitizeAxiosError(val);
      if (typeof val === 'string') return scrubString(val);
      if (val && typeof val === 'object') {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }
      return val;
    });
    if (raw == null) return '';
    return raw.length <= maxLength ? raw : raw.slice(0, maxLength) + '…(truncated)';
  } catch {
    // Serialization failures can themselves include values supplied by a caller.
    return '[Unserializable]';
  }
}

export type SanitizedAxiosError = {
  name?: unknown;
  message?: unknown;
  code?: unknown;
  stack?: unknown;
  config?: Record<string, unknown>;
  response?: Record<string, unknown>;
};

// Every returned field is a redacted clone, safe for direct console use.
export function sanitizeAxiosError(err: unknown): SanitizedAxiosError | unknown {
  if (!err || typeof err !== 'object') return typeof err === 'string' ? scrubString(err) : err;
  const e = err as Record<string, unknown>;
  const config = e.config as Record<string, unknown> | undefined;
  const response = e.response as Record<string, unknown> | undefined;
  const safe = {
    name: e.name, message: e.message, code: e.code, stack: e.stack,
    ...(config && { config: {
      url: config.url, method: config.method, baseURL: config.baseURL,
      params: config.params, headers: config.headers,
      // Request bodies are deliberately omitted from diagnostic errors.
    } }),
    ...(response && { response: {
      status: response.status, statusText: response.statusText,
      data: response.data, headers: response.headers,
    } }),
  };
  return JSON.parse(safeStringify(safe, { maxLength: Infinity }));
}

export function scrubUnknownToString(value: unknown, maxLength = 8192): string {
  const scrubbed = typeof value === 'string' ? scrubString(value) : safeStringify(value);
  return scrubbed.length <= maxLength ? scrubbed : scrubbed.slice(0, maxLength) + '…(truncated)';
}
